/**
 * Shared dependencies
 */
import type { List, ListType, DayOfTheWeek, RecipeListItem } from '../../shared/types/lists';

/**
 * Internal dependencies
 */
export type CreateUserListParams = {
	label: string;
	type: ListType;
	description?: string;
	fromDayOfTheWeek?: DayOfTheWeek;
	items?: RecipeListItem[];
};

type ListWithItems = Partial<List & { items: RecipeListItem[] }>;

const DEFAULT_BODY: ListWithItems = {
	description: '',
	fromDayOfTheWeek: 'MON',
	items: [],
};

/**
 * Creates a new list for the current user in the Valio API.
 * @link https://evermade.stoplight.io/docs/valio-app/351xv0sgcz9rc-post-users-user-id-lists
 * @param {Params} params - The parameters for creating the list.
 * @param {string} params.label - The label (name) of the list.
 * @param {ListType} params.type - The type of the list (e.g., RECIPE, FOLDER, SHOPPING_LIST, WEEK_MENU).
 * @param {string} [params.description] - (Optional) A description for the list.
 * @param {DayOfTheWeek} [params.fromDayOfTheWeek] - (Optional) The starting day of the week for the list.
 * @param {ListItem[]} [params.items] - (Optional) An array of items to include in the list.
 * @return {Promise<List>} A promise that resolves to the created user's list.
 */
export async function createUserList(params: CreateUserListParams): Promise<List> {
	const { host } = window.valioConfig;
	const { session } = window.valioApp;

	// Validate required params
	if (!params.label) {
		throw new Error('Label is required to create a user list.');
	}

	if (!params.type) {
		throw new Error('Type is required to create a user list.');
	}

	// Combine default body with provided params
	const body: ListWithItems = {
		...DEFAULT_BODY,
		...params,
	};

	// Ensure the session is initialized
	if (!session.initialized) {
		await session.init();
	}

	// Ensure the user is authenticated
	if (!session.userId) {
		return Promise.reject(new Error('User is not authenticated. Cannot create list.'));
	}

	const headers = await session.authHeader();

	// Make the API request to create the list
	return fetch(`${host}/api/v1/users/${session.userId}/lists`, {
		method: 'POST',
		headers,
		body: JSON.stringify(body),
	})
		.then(async (response) => {
			if (!response.ok) {
				// Handle HTTP errors like 401, 404, 500, etc.
				const errorText = await response.text();
				throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
			}

			return await response.json();
		})
		.catch((error) => {
			console.error('Error creating user list:', error);
			throw error;
		});
}
